import { useApprovedResources } from '@/features/copilot/tools/approval';
import { useCustomerFeedback } from '@/features/customer-feedback';
import { useNetworkStatus } from '@/hooks/browser/use-network-status';
import { useAnalytics } from '@/services/analytics';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { generatePath } from '@/services/router/generate-path';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import { usePermissions } from '@customerio/hydra-permissions/react';
import {
  SUPPORTED_FILE_TYPES,
  type CopilotMessage,
  type CopilotSession,
  type CopilotUploadFile,
} from '@customerio/hydra-copilot';
import {
  useCopilotFileUpload,
  useCopilotQueuedMessages,
  useCopilotStatus,
  useCopilotWebSearch,
} from '@customerio/hydra-copilot/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import {
  Banner,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  Form,
  Icon,
  InlineStack,
  KeyboardShortcut,
  Link,
  Paragraph,
  Stack,
  Strong,
  Text,
  TextArea,
  Tooltip,
  Truncated,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useCurrentWorkspace } from '@/context/current-workspace';
import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMatches } from 'react-router-dom';
import * as chatStyles from '../../chat.css';
import { Attachments } from '../attachments';
import { FileUploadArea, type FileUploadAreaRef } from '../file-upload-area';
import { uploadFilesWithRetry } from '../../helpers/file-utils';
import * as styles from '../../index.css';

function useTokenUsage(session: CopilotSession) {
  const messages = useStore(session.__messages);

  return useMemo(() => {
    // Scan backwards for the most recent meta message with token usage
    for (let index = messages.length - 1; index >= 0; index--) {
      const message = messages[index];
      if (message.role === 'model' && message.type === 'meta' && 'meta' in message) {
        const meta = message.meta;
        if (meta?.prompt_tokens_used != null && meta.prompt_tokens_used > 0) {
          return {
            promptTokensUsed: meta.prompt_tokens_used,
            contextWindowSize: meta.context_window_size ?? 0,
          };
        }
      }
    }
    return null;
  }, [messages]);
}

type TokenUsageProps = { session: CopilotSession };

function TokenUsage({ session }: TokenUsageProps) {
  const usage = useTokenUsage(session);

  if (usage == null) {
    return <Box />;
  }

  const { promptTokensUsed, contextWindowSize } = usage;
  const percentage = contextWindowSize > 0 ? Math.round((promptTokensUsed / contextWindowSize) * 100) : null;

  if (percentage == null || percentage < 80) {
    return <Box />;
  }

  const tooltipContent = `This conversation is using ${percentage}% of the available memory. As it grows, older details may be dropped to fit new messages.`;

  return (
    <Tooltip content={tooltipContent} isCustom={true}>
      {(context) => (
        <Paragraph
          text="product-pxs-secondary"
          color="subtle"
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        >
          {percentage}% memory used
        </Paragraph>
      )}
    </Tooltip>
  );
}

type FooterStatmentProps = { input: string };

function FooterStatment({ input }: FooterStatmentProps) {
  const networkStatus = useNetworkStatus();

  return (
    <Paragraph text="product-pxs-secondary" pt="050" color="subtle">
      {networkStatus === 'offline' ? (
        <>{input.length > 0 ? <>You can't send messages when offline</> : <>You are currently offline</>}</>
      ) : input.length > 0 ? (
        <>
          <Strong weight="medium">Shift + Return</Strong> to add a new line
        </>
      ) : (
        <>Agent is AI and can make mistakes. Please double-check responses.</>
      )}
    </Paragraph>
  );
}

type QueuedMessageProps = { messages: (CopilotMessage & { text: string })[] };

function QueuedMessage({ messages }: QueuedMessageProps) {
  if (messages.length === 0) {
    return null;
  }

  return (
    <Stack gap="050">
      {messages.map((m, index) => (
        <Text key={index} size="xs" color="subtle">
          <Truncated>{m.text}</Truncated>
        </Text>
      ))}
    </Stack>
  );
}

type InputProps = {
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  session: CopilotSession;
  onSubmit?: () => void;
  placeholder?: string;
  wrapperClassName?: string;
  resetSession?: () => CopilotSession;
};

export function Input({
  session,
  input,
  setInput,
  onSubmit,
  placeholder: customPlaceholder,
  wrapperClassName: customWrapperClassName,
  resetSession,
}: InputProps) {
  const lastInput = useRef<string | null>(null);
  const lastFiles = useRef<CopilotUploadFile[]>([]);
  const fileUploadAreaRef = useRef<FileUploadAreaRef>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const snackbars = useSnackbars();
  const { track } = useAnalytics();
  const status = useCopilotStatus(session);
  const isLoading =
    resetSession == null && (status === 'sending' || status === 'receiving' || status === 'reconnecting');
  const queuedMessages = useCopilotQueuedMessages(session);
  // Filter to only user-visible text messages — tool_response messages (e.g. from
  // auto-approve) are system messages that shouldn't appear in the queued messages UI.
  const visibleQueuedMessages = useMemo(
    () => queuedMessages.filter((m): m is CopilotMessage & { text: string } => m.type === 'text' && 'text' in m),
    [queuedMessages],
  );
  const networkStatus = useNetworkStatus();

  // File upload functionality
  const fileUpload = useCopilotFileUpload(session);

  // Handle files from both drag drop and file input
  const handleFiles = useCallback(
    (files: File[]) => {
      const result = fileUpload.addFiles(files);

      const messages: string[] = [];

      if (result.errors.length > 0) {
        messages.push(...result.errors.map((error) => `${error.name}: ${error.error}`));
      }

      if (messages.length > 0) {
        setUploadError(messages.join('\n'));
      }

      // Immediately start uploading the files with retry for transient network errors
      if (result.addedCount > 0) {
        const pendingFiles = session.getPendingUploadFiles();
        uploadFilesWithRetry(pendingFiles, fileUpload.uploadFile, fileUpload.removeFile)
          .then((uploadResult) => {
            // Handle failed uploads - accumulate with validation errors
            if (uploadResult.failed.length > 0) {
              const failedMessages = uploadResult.failed.map((f) => `${f.name}: ${f.error}`);

              // Append to existing validation errors if any
              setUploadError((previousError) => {
                if (!isEmptyString(previousError)) {
                  return previousError + '\n' + failedMessages.join('\n');
                }
                return failedMessages.join('\n');
              });

              // Report to Sentry
              for (const f of uploadResult.failed) {
                sentryCaptureException(new Error(`File upload failed for ${f.name}: ${f.error}`));
              }
            } else if (uploadResult.successful.length > 0 && messages.length === 0) {
              // Clear error if all uploads succeeded and there were no validation errors
              setUploadError(null);
            }

            if (uploadResult.successful.length > 0) {
              const fileTypes = uploadResult.successful.map((f) => {
                const extension = f.name.split('.').pop()?.toLowerCase() ?? 'unknown';
                return extension;
              });
              track('aiAssistant', 'fileUploaded', {
                Count: uploadResult.successful.length,
                'File Types': fileTypes,
              });
            }
          })
          .catch((error) => {
            // This catch should rarely be hit since uploadFilesWithRetry uses Promise.allSettled
            sentryCaptureException(error);
            setUploadError(error instanceof Error ? error.message : 'Failed to upload files');
          });
      }
    },
    [fileUpload, session, track],
  );

  const fileChips = useMemo(
    () =>
      fileUpload.files.map((file) => ({
        id: file.id,
        name: file.name,
        size: file.size,
        mimeType: file.mimeType,
        source: file.file,
        status: file.status,
      })),
    [fileUpload.files],
  );

  async function submit(input: string, files: CopilotUploadFile[] = []) {
    setError(null);
    try {
      // Create message payload
      const messages: CopilotMessage[] = [];

      // Add file messages for uploaded files
      const uploadedFiles = files.filter((f) => f.status === 'uploaded' && f.uploadedUrl != null);
      for (const file of uploadedFiles) {
        messages.push({
          role: 'user',
          type: 'content',
          content: file.uploadedUrl ?? '',
          mime_type: file.mimeType,
          display_name: file.name,
        });
      }

      // Add text message if there's input
      if (!isEmptyString(input)) {
        messages.push({
          role: 'user',
          type: 'text',
          text: input,
        });
      }

      // Clear input and files indicating that the user has sent the message
      setInput('');
      fileUpload.clearFiles();

      // If we have messages to send, send them
      if (messages.length > 0) {
        const targetSession = resetSession?.() ?? session;
        onSubmit?.();
        // Read messages imperatively (not via hook subscription) to avoid
        // re-rendering the input component on every streaming message update.
        const currentMessages = targetSession.__statefulMessages.get();
        const userTurnCount = currentMessages.filter((m) => m.role === 'user').length;
        await targetSession.sendMessage(messages);
        track('aiAssistant', 'messageSent', {
          Message: input,
          'Has Files': uploadedFiles.length > 0,
          'File Count': uploadedFiles.length,
          'Turn Number': userTurnCount + 1,
          'Is First Message': userTurnCount === 0,
          'Conversation ID': targetSession.id,
        });
      }
    } catch (error) {
      sentryCaptureException(error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  }

  const handleUploadButtonClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (fileUploadAreaRef.current != null) {
      fileUploadAreaRef.current.triggerFileInput();
    }
  }, []);

  const handleStop = useCallback(async () => {
    track('aiAssistant', 'stopClicked', {});
    const { messages, error } = await session.stop();

    // Always restore queued messages to the input box, even if the backend
    // cancellation failed — the local stream is already aborted either way.
    const texts = messages
      .filter((m): m is CopilotMessage & { text: string } => m.type === 'text' && 'text' in m)
      .map((m) => m.text);
    if (texts.length > 0) {
      // Use a functional updater so we always prepend onto the latest input
      // value — not the one captured when this callback was created. The
      // component becomes interactive as soon as the stream aborts (before
      // stopExchange resolves), so the user may have typed in the window
      // between abort and the await returning.
      const queued = texts.join('\n\n');
      setInput((previous) => [queued, previous].filter((t) => t.trim() !== '').join('\n\n'));
    }

    if (error != null) {
      sentryCaptureException(error);
      snackbars.create({
        message: 'Failed to stop the response. Please try again.',
        variant: 'error',
      });
    }
  }, [session, setInput, snackbars, track]);

  const { isAllApproved, setIsAllApproved } = useApprovedResources();

  // Live-data banner: shown when auto-approve is on (off uses the per-tool
  // approval banner instead). Reuses isLoading so the banner shares the
  // rest-of-UI lifecycle. Dismiss resets per turn.
  const allowAgentEditLiveData = useFeatureFlag(AccountFeatureFlag.AllowAgentEditLiveData);
  const { hasPermission } = usePermissions();
  const canEditAccount = hasPermission('account/edit') === true;
  const [liveDataBannerDismissed, setLiveDataBannerDismissed] = useState(false);
  useEffect(() => {
    if (!isLoading) {
      setLiveDataBannerDismissed(false);
    }
  }, [isLoading]);
  const showLiveDataBanner =
    allowAgentEditLiveData === true && isAllApproved === true && isLoading && !liveDataBannerDismissed;

  const enableWebSearch = useCopilotWebSearch(session);
  const workspace = useCurrentWorkspace();
  const safeNavigate = useSafeNavigate();
  const showFeedbackModal = useCustomerFeedback();
  const matches = useMatches();
  const lastMatch = matches[matches.length - 1];

  const hasContent = useMemo<boolean>(
    () => !isEmptyString(input) || fileUpload.files.length > 0,
    [input, fileUpload.files],
  );
  const hasUploadingFiles = useMemo<boolean>(
    () => fileUpload.files.some((f) => f.status === 'uploading' || f.status === 'pending'),
    [fileUpload.files],
  );
  const canSubmit = hasContent && !hasUploadingFiles;

  return (
    <>
      {uploadError != null ? (
        <Banner
          title="Upload Error"
          variant="error"
          mb="100"
          shouldShowDismissButton={true}
          onClose={() => setUploadError(null)}
          description={
            <Box as="span" whiteSpace="pre-line">
              {uploadError}
            </Box>
          }
        />
      ) : null}
      {error != null ? (
        <Banner
          title="Error"
          variant="error"
          mb="100"
          action={{
            label: 'Retry',
            onClick: () => {
              if (lastInput.current != null || lastFiles.current.length > 0) {
                submit(lastInput.current ?? '', lastFiles.current);
              }
            },
          }}
          shouldShowDismissButton={true}
          onClose={() => setError(null)}
          description={error}
        />
      ) : null}
      {status === 'reconnecting' ? (
        <Banner
          title="Reconnecting"
          variant="warning"
          mb="100"
          description="The connection was interrupted. Attempting to recover…"
        />
      ) : null}
      {isLoading && visibleQueuedMessages.length > 0 ? (
        <Box
          mb="100"
          p="100"
          display="flex"
          flexDirection="column"
          gap="075"
          className={chatStyles.queuedMessagesContainer}
        >
          <InlineStack
            alignBlock="center"
            alignInline="space-between"
            pb="050"
            className={chatStyles.queuedMessagesHeader}
          >
            <InlineStack alignBlock="center" gap="050">
              <Icon name="time-window" size="xs" color="subtle" />
              <Text size="xs" color="subtle" weight="medium">
                Queued messages
              </Text>
            </InlineStack>
            <InlineStack alignBlock="center" gap="050">
              <Text size="xs" color="subtle">
                Press
              </Text>
              <KeyboardShortcut hotkey="ArrowUp" />
              <Text size="xs" color="subtle">
                to edit
              </Text>
            </InlineStack>
          </InlineStack>
          <QueuedMessage messages={visibleQueuedMessages} />
        </Box>
      ) : null}
      {showLiveDataBanner ? (
        <Banner
          title="Allowing this action may edit live data."
          variant="caution"
          mb="100"
          onClose={() => setLiveDataBannerDismissed(true)}
          description={
            <Text size="xs">
              {canEditAccount ? (
                <>
                  You can{' '}
                  <Link href="/settings/ai" isUnderlined>
                    disable editing live campaigns
                  </Link>{' '}
                  in AI settings. You can also{' '}
                </>
              ) : (
                <>You can </>
              )}
              <Link
                href="#"
                isUnderlined
                onClick={(event) => {
                  event.preventDefault();
                  handleStop();
                }}
              >
                stop this request now
              </Link>
              .
            </Text>
          }
        />
      ) : null}
      <FileUploadArea
        ref={fileUploadAreaRef}
        onFilesSelected={handleFiles}
        disabled={networkStatus === 'offline'}
        style={{ position: 'relative' }}
        acceptedMimeTypes={Object.keys(SUPPORTED_FILE_TYPES)}
      >
        <Form
          onSubmit={async (event) => {
            event.preventDefault();

            if (!canSubmit) {
              return;
            }

            if (networkStatus === 'offline') {
              return;
            }

            // Only update retry refs if this is a direct send (not queued).
            // If the session is busy, sendMessage will queue — don't overwrite
            // the retry refs for the in-flight message.
            if (status !== 'sending' && status !== 'receiving') {
              lastInput.current = input;
              lastFiles.current = Array.from(fileUpload.files);
            }
            await submit(input, Array.from(fileUpload.files));
          }}
          position="relative"
          autoLoading={false}
        >
          <TextArea
            value={input}
            onInput={(event) => setInput(event.target.value)}
            onKeyDown={(event: KeyboardEvent) => {
              // Arrow up at the top of the input: pull all queued messages back into the editor
              if (event.key === 'ArrowUp' && visibleQueuedMessages.length > 0) {
                const textarea = event.target as HTMLTextAreaElement;
                const isAtTop = textarea.selectionStart === 0 && textarea.selectionEnd === 0;
                if (isAtTop) {
                  event.preventDefault();
                  const cleared = session.clearQueuedMessages();
                  const texts = cleared
                    .filter((m): m is CopilotMessage & { text: string } => 'text' in m && m.type === 'text')
                    .map((m) => m.text);
                  const restoredText = texts.join('\n');
                  const combined = [restoredText, input].filter((t) => t.trim() !== '').join('\n\n');
                  setInput(combined);
                }
              }
            }}
            onPaste={(event) => {
              // Extract image files from clipboard
              const items = Array.from(event.clipboardData?.items ?? []);
              const imageFiles = items
                .filter((item: DataTransferItem) => item.type.startsWith('image/'))
                .map((item: DataTransferItem) => item.getAsFile())
                .filter((file): file is File => file != null);

              if (imageFiles.length > 0) {
                event.preventDefault();
                handleFiles(imageFiles);
              }
            }}
            id="copilot-chat-input"
            placeholder={
              fileChips.length > 0 ? 'Add a message (optional)' : customPlaceholder ?? 'How can I help you today?'
            }
            shouldSubmitOnEnter={true}
            ariaLabel="Send message to Agent"
            resize="none"
            minRows={1}
            maxRows={6}
            unsafe_textareaWrapperClassName={[
              styles.chatTextareaWrapper,
              fileChips.length > 0 ? styles.chatTextareaWrapperWithFiles : '',
              customWrapperClassName ?? '',
            ]
              .filter(Boolean)
              .join(' ')}
            unsafe_inputClassName={styles.chatTextareaInput}
          />

          {/* File chips positioned absolutely */}
          {fileChips.length > 0 ? (
            <Box
              style={{
                position: 'absolute',
                top: themeVars.space['100'],
                left: themeVars.space['100'],
                right: themeVars.space['600'],
                zIndex: 1,
                pointerEvents: 'none',
              }}
            >
              <Box style={{ pointerEvents: 'auto' }}>
                <Attachments
                  files={fileChips}
                  variant="inline"
                  readOnly={false}
                  onRemove={fileUpload.removeFile}
                  session={session}
                />
              </Box>
            </Box>
          ) : null}

          <Box
            display="flex"
            alignItems="flex-end"
            justifyContent="space-between"
            style={{
              position: 'absolute',
              bottom: themeVars.space['100'],
              left: themeVars.space['100'],
              right: themeVars.space['100'],
              pointerEvents: 'none',
            }}
          >
            <Box display="flex" alignItems="center" gap="050" style={{ pointerEvents: 'auto' }}>
              <Button
                icon="paperclip"
                isIconOnly={true}
                variant="tertiary"
                type="button"
                size="sm"
                style={{ border: 'none' }}
                isDisabled={networkStatus === 'offline'}
                onClick={handleUploadButtonClick}
              />
              <DropdownMenu>
                <DropdownMenuTriggerButton
                  isIconOnly
                  icon="properties"
                  variant="tertiary"
                  size="sm"
                  style={{ border: 'none' }}
                />
                <DropdownMenuContent>
                  <DropdownMenuGroup label="Capabilities">
                    <DropdownMenuCheckboxItem
                      isChecked={isAllApproved}
                      onCheckedChange={(checked) => {
                        setIsAllApproved(checked);
                        track('aiAssistant', 'applyChangesToggled', { Enabled: checked });
                      }}
                      shouldCloseOnClick={false}
                    >
                      Apply changes automatically
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                      isChecked={enableWebSearch}
                      onCheckedChange={(checked) => {
                        session.setWebSearchEnabled(checked);
                        track('aiAssistant', 'webSearchToggled', { Enabled: checked });
                      }}
                      shouldCloseOnClick={false}
                    >
                      Enable web search
                    </DropdownMenuCheckboxItem>
                  </DropdownMenuGroup>
                  <DropdownMenuGroup label="Settings">
                    {workspace != null ? (
                      <DropdownMenuItem
                        isDisabled={lastMatch?.id === 'workspace_settings_ai_business_profile'}
                        onClick={() => {
                          track('aiAssistant', 'editBusinessProfileClicked', { 'Click Location': 'menu' });
                          safeNavigate(
                            generatePath('/workspaces/:workspaceId/settings/ai-business-profile', {
                              workspaceId: workspace.id,
                            }),
                          );
                        }}
                      >
                        Add context
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem
                      onClick={() => {
                        track('aiAssistant', 'feedbackLinkClicked', {});
                        void showFeedbackModal({
                          feature: 'AI Agent',
                          onSubmit: (values) => {
                            track('aiAssistant', 'feedbackFormSubmitted', {
                              Feedback: values.feedback,
                            });
                          },
                        });
                      }}
                    >
                      Give feedback
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              <TokenUsage session={session} />
            </Box>
            <Box display="flex" alignItems="center" gap="050" style={{ pointerEvents: 'auto' }}>
              {isLoading ? (
                <Button
                  icon="stop-filled"
                  isIconOnly={true}
                  variant="secondary"
                  type="button"
                  size="sm"
                  onClick={handleStop}
                  className={styles.chatActionButton}
                />
              ) : (
                <Button
                  icon="arrow-up"
                  isIconOnly={true}
                  variant="primary"
                  type="submit"
                  size="sm"
                  isDisabled={networkStatus === 'offline' || !canSubmit}
                  className={styles.chatActionButton}
                />
              )}
            </Box>
          </Box>
        </Form>
      </FileUploadArea>
      <FooterStatment input={input} />
    </>
  );
}
